import { useApp } from './useApp';

interface UsePermissionsReturn {
  role: null;
  roleName: null;
  permissions: string[];
  isLoading: boolean;
  isSystemAdmin: boolean;
  isOwner: boolean;
  hasPermission: (code: string) => boolean;
  hasPermissionOrRole: (code: string, roles?: readonly string[]) => boolean;
  hasAnyPermission: (codes?: string[]) => boolean;
  hasAllPermissions: (codes?: string[]) => boolean;
}

export function usePermissions(): UsePermissionsReturn {
  const { user, loading } = useApp();

  // Permissions are resolved server-side and returned with /api/auth/me.
  // No extra API calls needed — AppContext already has this data.
  const permissions: string[] = user?.permissions ?? [];

  // System admin fallback: users with role='admin' retain full access even if
  // their user_roles record is missing in the DB (e.g. fresh install before seed).
  const isSystemAdmin = user?.role === 'admin';

  // Rol contextual en la empresa activa, mismo eje que usa el backend.
  //
  // `??` y NUNCA `||`: un asesor sin empresa activa tiene `effectiveRole: ''`
  // (sentinel), y `'' || user.role` caería al `role` legacy — enseñando la pantalla
  // a quien el backend rechaza. Con `??`, '' gana y se oculta, como debe.
  const isOwner = (user?.effectiveRole ?? user?.role) === 'owner';

  // `owner` entra en el bypass. Antes NO estaba, y el comentario que lo justificaba
  // decía «en `requireRole` no hay bypass de admin» — cierto entonces, ya no: el gate
  // de escritura del backend pasó a `requireEffectiveRoleOrPermission`, que sí da
  // bypass a admin y owner por igual (verix-api, fix del 403 al crear productos).
  //
  // Mantenerlo fuera dejaba de ser conservador y pasaba a ser un fallo propio: en
  // `POST /companies` todas las siembras son best-effort, así que si `seedCompanyRbac`
  // falla el owner se queda con `permissions: []`, sin bypass, y CADA `PermissionGuard`
  // le deniega — un ERP vacío y en silencio. Por `/register` no pasaba porque allí el
  // rol es `admin` y la red de seguridad de arriba sí aplicaba.
  const bypassesPermissions = isSystemAdmin || isOwner;

  const hasPermission = (code: string): boolean => {
    if (bypassesPermissions) return true;
    if (!code) return true;
    return permissions.includes(code);
  };

  // Espejo de `passesEffectiveRoleOrPermission` en verix-api: además del permiso, pasa quien
  // tenga en la empresa activa un rol de `roles`. Con `??`, como `isOwner`: el
  // `effectiveRole: ''` de un asesor sin empresa no cae al rol legacy.
  const hasPermissionOrRole = (code: string, roles: readonly string[] = []): boolean => {
    const rol = user?.effectiveRole ?? user?.role;
    return (!!rol && roles.includes(rol)) || hasPermission(code);
  };

  const hasAnyPermission = (codes: string[] = []): boolean => {
    if (bypassesPermissions) return true;
    if (!codes || codes.length === 0) return true;
    return codes.some(c => permissions.includes(c));
  };

  const hasAllPermissions = (codes: string[] = []): boolean => {
    if (bypassesPermissions) return true;
    if (!codes || codes.length === 0) return true;
    return codes.every(c => permissions.includes(c));
  };

  return {
    role: null,
    roleName: null,
    permissions,
    isLoading: loading,
    isSystemAdmin,
    isOwner,
    hasPermission,
    hasPermissionOrRole,
    hasAnyPermission,
    hasAllPermissions,
  };
}

export default usePermissions;
